import useActiveDocument from "../../document/hooks/useActiveDocument";
import useAnnotations from "../../annotation";
import { useMultipleGroupBlocks } from "../../configMap";
import { useCallback, useState } from "react";
import useDownloadPdf from "../../common/downloadPdf/hooks/useDownloadPdf";
import { useAppSelector } from "../../app";
import useUserSettings from "../../user/hooks/useUserSettings";
import useCategorizationAnnotations from "../../metadata/hooks/useCategorizationAnnotations";
import { useDispatch } from "react-redux";
import { setDrawerTypeAndOpen } from "../store/AnnotationSideControlsSlice";
import { DrawerType } from "../interfaces/drawer";
import { ADMIN_ROLE_ID } from "../../common/utilities/env";
import { Status } from "../../common/status/status";
import useActiveDocumentSet from "../../documentSet/hooks/useActiveDocumentSet";
import { notification } from "antd";
import { useTranslation } from "react-i18next";
import useNavigateWithState from "../../common/navigate/hooks/useNavigateWithState";
import { calculateHideDelay } from "../../common/utilities/text";
import { BULK_ACTION_DISABLED_STATUSES } from "../../documentSet/constants";
import http from "../../common/utilities/HttpModule";
import useAnnotationNormalization from "../../configMap/hooks/useAnnotationNormalization";
import { mapToEntityAnnotationDto } from "../../annotation/utils/annotationMappers";
import useDocumentTypes from "../../documentTypes/hooks/useDocumentTypes";
import { AxiosError } from "axios";

const useActions = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["annotationSideControls", "notifications"]);
  const { navigateWithState } = useNavigateWithState();

  const [open, setOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingAction, setProcessingAction] = useState<
    "reprocess" | "reexport" | null
  >(null);

  const { activeDocument } = useActiveDocument();

  const activeDocumentSet = useAppSelector(
    (state) => state.documentSetsReducer.activeDocumentSet,
  );
  const user = useAppSelector((state) => state.userReducer.user);
  const { updateAssigneeAndStatus } = useActiveDocumentSet();

  const { removeAllAnnotations, annotations } = useAnnotations({
    documentId: activeDocument?.id,
  });

  const { normalizeEntityAnnotationsInBulk } = useAnnotationNormalization();

  const { resetMultipleGroupBlocks } = useMultipleGroupBlocks();

  const { resetCategorizationAnnotations } = useCategorizationAnnotations();

  const downloadPdf = useDownloadPdf();

  const hideNonRequiredEntities = useAppSelector(
    (state) => state.userReducer.userSettings.hideNonRequiredEntities,
  );

  const { toggleHideNonRequiredEntitiesForUser } = useUserSettings();

  const { documentTypes } = useDocumentTypes();

  const removeAllAnnotationsAndGroupBlocks = useCallback(() => {
    removeAllAnnotations();
    resetMultipleGroupBlocks();
    resetCategorizationAnnotations();
  }, [
    removeAllAnnotations,
    resetMultipleGroupBlocks,
    resetCategorizationAnnotations,
  ]);

  const downloadPdfAndClose = () => {
    downloadPdf(
      activeDocument?.id || "",
      activeDocument?.name || "",
      activeDocument?.mimeType,
    );
    setOpen(false);
  };

  const removeAllAnnotationsAndClose = () => {
    removeAllAnnotationsAndGroupBlocks();
    setOpen(false);
  };

  const openDrawerAndSetType = useCallback(
    (drawerType: DrawerType) => {
      setOpen(false);
      dispatch(setDrawerTypeAndOpen(drawerType));
    },
    [dispatch],
  );

  const handleReExport = useCallback(async () => {
    if (!activeDocumentSet || isProcessing) {
      return;
    }

    setOpen(false); // Close menu immediately
    setIsProcessing(true);
    setProcessingAction("reexport");

    try {
      await updateAssigneeAndStatus(activeDocumentSet, Status.Approved);

      notification.success({
        title: t("notifications:documentsReExported.success.message"),
        description: t("notifications:documentsReExported.success.description"),
        duration: calculateHideDelay(
          t("notifications:documentsReExported.success.description"),
        ),
        onClose: () => {
          navigateWithState("/archive");
        },
      });
    } catch (error) {
      console.error("Failed to re-export document:", error);

      notification.error({
        title: t("notifications:documentsReExported.error.message"),
        description: t("notifications:documentsReExported.error.description"),
        duration: calculateHideDelay(
          t("notifications:documentsReExported.error.description"),
        ),
      });
    } finally {
      setIsProcessing(false);
      setProcessingAction(null);
    }
  }, [
    activeDocumentSet,
    updateAssigneeAndStatus,
    t,
    navigateWithState,
    isProcessing,
  ]);

  const handleReProcess = useCallback(
    async (documentTypeId?: string) => {
      if (!activeDocumentSet || isProcessing) {
        return;
      }

      setIsProcessing(true);
      setProcessingAction("reprocess");

      try {
        // Single document endpoint: open to all users, unlike the admin-only bulk endpoint.
        const url = `api/v1/document-sets/${activeDocumentSet.id}/reprocess`;
        await http.post(url, null, { params: { documentTypeId } });

        notification.success({
          key: "reprocessToast",
          title: t(
            "notifications:documentsReprocessed.success.message",
            "Documents reprocessed successfully",
          ),
          description: t(
            "notifications:documentsReprocessed.success.description",
            "The selected documents have been queued for reprocessing",
          ),
          duration: calculateHideDelay(
            t("notifications:documentsReprocessed.success.description"),
          ),
          onClose: () => {
            navigateWithState("/");
          },
        });
      } catch (e) {
        console.error("Failed to reprocess document:", e);

        // `http` is the axios instance, so a rejection here is an AxiosError.
        const error = e as AxiosError;

        // Check if it's a 404 error indicating no reprocessing config
        if (error?.response?.status === 404) {
          notification.error({
            key: "reprocessToast",
            title: t(
              "notifications:documentsReprocessed.noConfig.message",
              "Reprocessing not configured",
            ),
            description: t(
              "notifications:documentsReprocessed.noConfig.description",
              "No reprocessing configuration found for the selected documents. Please contact your administrator to set up reprocessing for this input.",
            ),
            duration: calculateHideDelay(
              t("notifications:documentsReprocessed.noConfig.description"),
            ),
          });
        } else {
          notification.error({
            key: "reprocessToast",
            title: t(
              "notifications:documentsReprocessed.error.message",
              "Failed to reprocess documents",
            ),
            description: t(
              "notifications:documentsReprocessed.error.description",
              "An error occurred while reprocessing the documents",
            ),
            duration: calculateHideDelay(
              t("notifications:documentsReprocessed.error.description"),
            ),
          });
        }
      } finally {
        setOpen(false);
        setIsProcessing(false);
        setProcessingAction(null);
      }
    },
    [activeDocumentSet, t, navigateWithState, isProcessing],
  );

  const handleRenormalizeAllAnnotations = useCallback(async () => {
    if (!annotations || annotations.length === 0) {
      notification.info({
        title: t(
          "notifications:noAnnotationsToRenormalize.message",
          "No annotations to renormalize",
        ),
        description: t(
          "notifications:noAnnotationsToRenormalize.description",
          "There are no annotations available for renormalization.",
        ),
        duration: calculateHideDelay(
          t("notifications:noAnnotationsToRenormalize.description"),
        ),
      });
      setOpen(false);
      return;
    }

    try {
      // Filter annotations that have normalization configurations
      const annotationsToNormalize = annotations
        .filter(
          (annotation) =>
            annotation.entity?.entityNormalizations &&
            annotation.entity.entityNormalizations.length > 0,
        )
        .map((annotation) =>
          mapToEntityAnnotationDto(
            annotation.id,
            annotation.values.join(" "),
            annotation.index || 1,
            annotation.entity.entityNormalizations ?? [],
            annotation.entity.id,
            annotation.multipleGroupBlocks,
          ),
        );

      if (annotationsToNormalize.length === 0) {
        notification.info({
          title: t(
            "notifications:noAnnotationsToRenormalize.message",
            "No annotations to renormalize",
          ),
          description: t(
            "notifications:noAnnotationsToRenormalize.description",
            "There are no annotations available for renormalization.",
          ),
          duration: calculateHideDelay(
            t("notifications:noAnnotationsToRenormalize.description"),
          ),
        });
        setOpen(false);
        return;
      }

      // Close menu immediately - annotations will display loading states during normalization
      setOpen(false);

      await normalizeEntityAnnotationsInBulk(annotationsToNormalize, true);
    } catch (error) {
      console.error("Failed to renormalize annotations:", error);
      notification.error({
        title: t(
          "notifications:annotationsRenormalized.error.message",
          "Renormalization failed",
        ),
        description: t(
          "notifications:annotationsRenormalized.error.description",
          "An error occurred while renormalizing annotations.",
        ),
      });
      setOpen(false);
    }
  }, [annotations, normalizeEntityAnnotationsInBulk, t]);

  // Visibility flags
  const showReExport =
    activeDocumentSet?.status === Status.Exported &&
    user?.access?.includes(ADMIN_ROLE_ID);

  // Typed as `Array<Status | undefined>` so a missing status can be passed to
  // `includes` directly: it is never a member, so the check keeps returning
  // false for it, exactly as before.
  const reProcessDisabledStatuses: Array<Status | undefined> = [
    ...BULK_ACTION_DISABLED_STATUSES,
    Status.Exported,
  ];

  // Every user can reprocess the document they are viewing. Bulk reprocess in
  // the overview stays admin only.
  const showReProcess = !reProcessDisabledStatuses.includes(
    activeDocumentSet?.status,
  );

  const isAssignedToCurrentUser =
    !activeDocumentSet?.assignee ||
    activeDocumentSet?.assignee?.id === user?.id;

  return {
    downloadPdfAndClose,
    removeAllAnnotationsAndClose,
    handleRenormalizeAllAnnotations,
    toggleHideNonRequiredEntitiesForUser,
    hideNonRequiredEntities,
    open,
    setOpen,
    activeDocument,
    openDrawerAndSetType,
    handleReExport,
    handleReProcess,
    showReExport,
    showReProcess,
    isAssignedToCurrentUser,
    isProcessing,
    processingAction,
    documentTypes,
  };
};

export default useActions;
